import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AnimatedSection, {
  staggerContainer,
  fadeUpVariant,
} from "../components/AnimatedSection";
import LearnMoreArrowIcon from "../components/LearnMoreArrowIcon";
import "./InnerPage.scss";

const pillars = [
  {
    title: "Shipment-Level Capital",
    desc: "We finance individual shipments, not companies. Every trade is assessed and funded on its own merits.",
  },
  {
    title: "Execution Monitoring",
    desc: "We track trades in motion — from loading to delivery. Capital stays attached to execution.",
  },
  {
    title: "Settlement Infrastructure",
    desc: "Payments triggered on delivery. No manual invoicing, no 60-day waits. Just settlement.",
  },
  {
    title: "Corridor Intelligence",
    desc: "We build data on African freight corridors — routes, counterparties, and risk patterns — over every trade we fund.",
  },
];

const whyPoints = [
  "African freight moves billions of dollars of goods annually, yet most trades are still funded informally — with cash, trust, and exposure.",
  "The continent's trade corridors are some of the fastest-growing in the world. Intra-African trade is accelerating under the AfCFTA.",
  "There is no institutional-grade capital infrastructure designed for African short-haul freight. We are building it.",
  "Every trade we finance is data. That data makes the next trade cheaper, safer, and faster to fund.",
];

const stats = [
  { num: "$1", unit: "T+", label: "African Trade Volume (annual)" },
  { num: "54", unit: "", label: "Countries in AfCFTA" },
  { num: "30", unit: "%", label: "Intra-Africa Trade Growth" },
  { num: "∞", unit: "", label: "Corridor Potential" },
];

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main>
        <section className="about-page__mission">
          <div className="container">
            <AnimatedSection className="about-page__mission-inner">
              <p className="about-page__mission-label">
                Our Mission
              </p>
              <h1 className="about-page__mission-title">
                We're building the capital layer behind{" "}
                <span>African freight</span>
              </h1>
              <p className="about-page__mission-sub">
                OneLedger converts fragmented trade flows into a scalable,
                reliable credit and execution platform — one shipment at a time.
              </p>
            </AnimatedSection>
          </div>
        </section>

        <section className="about-page__building">
          <div className="container">
            <AnimatedSection className="about-page__building-grid">
              <div className="about-page__building-text">
                <p className="inner-page__label">What We're Building</p>
                <h2>Plain language: what OneLedger does</h2>
                <p>
                  A trader wants to move a shipment from Lagos to Accra. They
                  need capital now. Their buyer will pay in 30 days. That gap
                  kills deals and stalls growth.
                </p>
                <p>
                  OneLedger steps in. We assess the shipment, fund it, monitor
                  its journey, and trigger payment on delivery. The trader gets
                  capital on day one. The buyer pays on receipt. Everyone moves
                  forward.
                </p>
                <p>
                  We are not a bank. We are not a logistics company. We are the
                  financial infrastructure that makes each trade work — the
                  layer that converts a fragmented corridor into a reliable,
                  fundable, repeatable business.
                </p>
              </div>
              <div className="about-page__pillars">
                {pillars.map(({ title, desc }) => (
                  <div key={title} className="about-page__pillar">
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>

        <section className="about-page__why">
          <div className="container">
            <AnimatedSection className="about-page__why-grid">
              <div className="about-page__why-text">
                <p className="inner-page__label">Why Africa, Why Now</p>
                <h2>
                  The moment is now. The infrastructure isn't ready. We're
                  changing that.
                </h2>
                <div className="about-page__why-points">
                  {whyPoints.map((point, i) => (
                    <div key={i} className="about-page__why-point">
                      <div className="about-page__why-point-dot" />
                      <p>{point}</p>
                    </div>
                  ))}
                </div>
              </div>

              <motion.div
                className="about-page__why-visual"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {stats.map(({ num, unit, label }) => (
                  <motion.div
                    key={label}
                    className="about-page__stat-card"
                    variants={fadeUpVariant}
                  >
                    <div className="about-page__stat-card-num">
                      {num}
                      <span>{unit}</span>
                    </div>
                    <div className="about-page__stat-card-label">{label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatedSection>
          </div>
        </section>

        <section className="inner-page__section inner-page__section--cta">
          <div className="container inner-page__cta">
            <AnimatedSection>
              <p className="inner-page__label">We're early</p>
              <h2 className="inner-page__cta-title">
                We're speaking with traders, brokers, and credit professionals
                across West Africa.
              </h2>
              <Link to="/contact" className="btn-learn-more">
                Start a Conversation
                <LearnMoreArrowIcon />
              </Link>
            </AnimatedSection>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
